import { captureMessage, captureReactException } from "@sentry/react";

import { isSentryEnabled } from "@src/observability";

// severity -> `console` method name for dynamic lookup.
const CONSOLE_METHOD_BY_LEVEL = {
  fatal: "error",
  error: "error",
  warning: "warn",
  info: "info",
  log: "log",
  debug: "debug",
};

function logMessageToConsole({ message, level, tags }) {
  const eventId = crypto.randomUUID();
  const payload =
    tags && Object.keys(tags).length > 0 ? { message, tags } : message;

  const method = CONSOLE_METHOD_BY_LEVEL[level] ?? "info";
  const logFn = console[method] ?? console.info;

  logFn.call(console, "[reportMessage]", payload);

  return eventId;
}

/**
 * Sends a structured message to the active observability backend. When
 * observability is disabled we log to the console instead.
 *
 * @param {object} params
 * @param {string} params.message
 * @param {string} [params.level] Severity (e.g. `"error"`, `"info"`).
 * @param {Record<string, string>} [params.tags]
 * @returns {string} Event id.
 */
export function reportMessage({ message, level, tags }) {
  if (!isSentryEnabled()) {
    return logMessageToConsole({ message, level, tags });
  }

  return captureMessage(message, { level, tags });
}

/**
 * Captures an error from a React error boundary (react-error-boundary `info`
 * shape is compatible with Sentry's expected `{ componentStack }`).
 *
 * @param {object} params
 * @param {Error} params.error
 * @param {{ componentStack?: string }} params.info
 * @returns {string} Event id.
 */
export function reportReactErrorBoundary({ error, info }) {
  if (!isSentryEnabled()) {
    return logMessageToConsole({
      message: "reactErrorBoundary",
      level: "error",
      tags: { error, info },
    });
  }

  return captureReactException(error, info);
}
